import React from "react";
import { PAGES } from "@gonoodle/gn-universe-analytics-schema/src/constants";
import { Newsletter, Toast } from "@gonoodle/gn-universe-ui";
import { useRouter } from "next/router";
import { QueryClient, useQuery } from "react-query";
import { dehydrate } from "react-query/hydration";

import api from "../api";
import Layout from "../components/Layout";
import { QUERY_KEYS, ROUTE_PERMISSIONS } from "../constants";
import { withAuthorization } from "../containers";
import { useLogEvent } from "../contexts/Analytics";
import { appConfigActionsTypes, useAppConfig } from "../contexts/appConfig";
import { useUser } from "../contexts/user";
import { getUtmParamsFromQueryParams } from "../utils/analytics";
import LayoutRenderer from "../components/LayoutRenderer";

const client = api();

const Home = () => {
  const router = useRouter();
  const { user, updateUser } = useUser();
  const [appConfigState, appConfigDispatch] = useAppConfig();
  const alert = Toast.useAlert();

  React.useEffect(() => {
    if (appConfigState.toasts.logout) {
      alert.success("You've been successfully logged out");
      appConfigDispatch({
        type: appConfigActionsTypes.SET_LOGOUT_TOAST,
        payload: false,
      });
    }
  }, [alert, appConfigDispatch, appConfigState.toasts.logout]);

  const { data: layout } = useQuery(
    [QUERY_KEYS.LAYOUT],
    () => client.getLayout(),
    {
      staleTime: Infinity,
    },
  );

  const { logEvent: logNewsletterSignUpEvent } = useLogEvent({
    event: "Newsletter Sign Up",
    properties: {
      ...getUtmParamsFromQueryParams(router.query),
    },
    options: {
      enabled: false,
      referrer: {
        sourcePage: PAGES.HOME,
        sourcePageType: PAGES.HOME,
      },
    },
  });

  const handleNewsletterSignup = (userInfo) => {
    updateUser(userInfo);
    logNewsletterSignUpEvent();
  };

  return (
    <Layout pageTitle="Home" pageDescription="Home Page" progressBar>
      <div className="container pt-sm pb-lg flex flex-col gap-8 md:gap-10 lg:gap-12">
        <LayoutRenderer layout={layout} />
      </div>

      {!user.isAnonymous && !user.isLoggedIn && (
        <Newsletter handleSubmit={handleNewsletterSignup} />
      )}
    </Layout>
  );
};

export const getServerSideProps = withAuthorization(async (ctx) => {
  const serverClient = api(ctx);
  const queryClient = new QueryClient();

  await queryClient.prefetchQuery(QUERY_KEYS.LAYOUT, async () =>
    serverClient.getLayout(),
  );

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
    },
  };
}, ROUTE_PERMISSIONS.PUBLIC);

export default Home;
